import {DefaultTheme, useTheme} from 'styled-components';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda-adjunct';

import {
  ChartEncodingLegendIconType,
  ChartEncodingLegendItem,
} from '../types/ChartEncodingLegendItem';
import {getBarHighlightPaths} from '../utils/getBarHighlightPaths';
import {
  getScatterDoughnutRingRadius,
  getScatterDoughnutStrokeWidth,
} from '../utils/getScatterDoughnutStrokeWidth';
import {
  getScatterTrianglePoints,
  getScatterTriangleRingPath,
} from '../utils/getScatterTrianglePoints';

const LEGEND_ICON_SIZE = 12;
const LEGEND_SHAPE_SIZE = 6;
const LEGEND_VIEWBOX_SIZE = 24;
const LEGEND_SHAPE_CENTER = LEGEND_VIEWBOX_SIZE / 2;
const LEGEND_BAR_WIDTH = 10;
const LEGEND_BAR_HEIGHT = 14;
const LEGEND_BAR_X = (LEGEND_VIEWBOX_SIZE - LEGEND_BAR_WIDTH) / 2;
const LEGEND_BAR_Y = (LEGEND_VIEWBOX_SIZE - LEGEND_BAR_HEIGHT) / 2;

interface ChartEncodingLegendIconProps {
  item: ChartEncodingLegendItem;
}

export function ChartEncodingLegendIcon(props: ChartEncodingLegendIconProps) {
  const theme = useTheme();
  const fillColor = theme.colors.palettes.orange['60']['100'];

  return (
    <svg
      width={LEGEND_ICON_SIZE}
      height={LEGEND_ICON_SIZE}
      viewBox={`0 0 ${LEGEND_VIEWBOX_SIZE} ${LEGEND_VIEWBOX_SIZE}`}
      aria-hidden
    >
      {renderIcon(props.item.icon, fillColor, theme)}
    </svg>
  );
}

function renderIcon(icon: ChartEncodingLegendIconType, fillColor: string, theme: DefaultTheme) {
  return match(icon)
    .with('circle', () => (
      <circle
        cx={LEGEND_SHAPE_CENTER}
        cy={LEGEND_SHAPE_CENTER}
        r={LEGEND_SHAPE_SIZE}
        fill={fillColor}
      />
    ))
    .with('doughnut', () => (
      <circle
        cx={LEGEND_SHAPE_CENTER}
        cy={LEGEND_SHAPE_CENTER}
        r={getScatterDoughnutRingRadius(LEGEND_SHAPE_SIZE)}
        fill="none"
        stroke={fillColor}
        strokeWidth={getScatterDoughnutStrokeWidth(LEGEND_SHAPE_SIZE)}
      />
    ))
    .with('triangle-increased', () => (
      <polygon
        points={getScatterTrianglePoints(
          LEGEND_SHAPE_CENTER,
          LEGEND_SHAPE_CENTER,
          'increased',
          LEGEND_SHAPE_SIZE
        )}
        fill={fillColor}
      />
    ))
    .with('triangle-decreased', () => (
      <polygon
        points={getScatterTrianglePoints(
          LEGEND_SHAPE_CENTER,
          LEGEND_SHAPE_CENTER,
          'decreased',
          LEGEND_SHAPE_SIZE
        )}
        fill={fillColor}
      />
    ))
    .with('triangle-ring-increased', () => (
      <path
        d={getScatterTriangleRingPath(
          LEGEND_SHAPE_CENTER,
          LEGEND_SHAPE_CENTER,
          'increased',
          LEGEND_SHAPE_SIZE
        )}
        fill={fillColor}
        fillRule="evenodd"
      />
    ))
    .with('triangle-ring-decreased', () => (
      <path
        d={getScatterTriangleRingPath(
          LEGEND_SHAPE_CENTER,
          LEGEND_SHAPE_CENTER,
          'decreased',
          LEGEND_SHAPE_SIZE
        )}
        fill={fillColor}
        fillRule="evenodd"
      />
    ))
    .with('border-new', () => renderBorderIcon(fillColor, theme.colors.palettes.green['60']['100']))
    .with('border-suspicious', () => renderBorderIcon(fillColor, theme.colors.severity.danger))
    .with('border-dealer', () =>
      renderBorderIcon(fillColor, theme.colors.palettes.blue['60']['100'])
    )
    .with('bar', () => renderBarIcon({fillColor}))
    .with('bar-hole', () => renderBarIcon({fillColor, showPrivateSellerHole: true}))
    .with('bar-caret-increased', () =>
      renderBarIcon({fillColor, priceChangeDirection: 'increased'})
    )
    .with('bar-notch-decreased', () =>
      renderBarIcon({fillColor, priceChangeDirection: 'decreased'})
    )
    .with('bar-cap-new', () =>
      renderBarIcon({fillColor, capColor: theme.colors.palettes.green['60']['100']})
    )
    .with('bar-cap-suspicious', () =>
      renderBarIcon({fillColor, capColor: theme.colors.severity.danger})
    )
    .with('bar-cap-dealer', () =>
      renderBarIcon({fillColor, capColor: theme.colors.palettes.blue['60']['100']})
    )
    .exhaustive();
}

function renderBorderIcon(fillColor: string, strokeColor: string) {
  return (
    <circle
      cx={LEGEND_SHAPE_CENTER}
      cy={LEGEND_SHAPE_CENTER}
      r={LEGEND_SHAPE_SIZE}
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth={2}
    />
  );
}

interface RenderBarIconParams {
  fillColor: string;
  priceChangeDirection?: 'increased' | 'decreased';
  showPrivateSellerHole?: boolean;
  capColor?: string;
}

function renderBarIcon(params: RenderBarIconParams) {
  const paths = getBarHighlightPaths({
    x: LEGEND_BAR_X,
    y: LEGEND_BAR_Y,
    width: LEGEND_BAR_WIDTH,
    height: LEGEND_BAR_HEIGHT,
    priceChangeDirection: params.priceChangeDirection ?? null,
    showPrivateSellerHole: params.showPrivateSellerHole ?? false,
    hasCap: isNotNil(params.capColor),
  });

  return (
    <g>
      <path d={paths.bodyPath} fill={params.fillColor} fillRule={paths.fillRule} />
      {isNotNil(paths.capPath) && isNotNil(params.capColor) ? (
        <path d={paths.capPath} fill={params.capColor} />
      ) : null}
    </g>
  );
}
